import { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';

import saveAs from 'file-saver';
import { FormikProps } from 'formik';

import { Grid2 as Grid } from '@mui/material';

import { User } from '@/models/permission/AuthorityModel.ts';
import { Card, Question } from '@/models/card/CardModel';
import { Comment } from '@/models/CommentModel.ts';
import { Board } from '@/models/BoardModel.ts';
import { Step } from '@/models/folder/StepModel';

import { selectActiveBoard, selectRoomMembers } from '../../ducks/Boards';
import {
  selectCard,
  selectAttachments,
  selectComments,
  selectLog,
  selectIsUploadingAttachments,
  selectIsDeletingAttachments,
  selectPossibleResponsible,
  actions as cardsActions,
} from '../../ducks/Cards';
import { downloadCardAttachment } from '../../services/Questions';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as cardActions from '../../thunks/Cards';
import {
  calculateDisabledFieldsForCard,
  getUpdatedFieldsAndValues,
  getRequiredFields,
  populateInitialValues,
  hasResponsibleField,
  getCardMetaInfo,
  isFieldChanged,
} from '../../utils/FieldUtil';
import { validateAnsweredDateField, validateAnswerField } from '../../utils/Validators';
import { withViewModeAndStepQuery } from '../../utils/ViewModeUtil';

import CardAttachments from '../common/Attachments';
import CardComments from '../common/Comments';
import Form from '../common/Form';
import Log from '../common/Log';
import Error from '../../common/Error';
import PageWrapper from '../../common/PageWrapper';
import CardStep from './CardStep';

const mapStateToProps = (state: any, ownProp: any) =>
  createStructuredSelector({
    board: selectActiveBoard(),
    card: selectCard({ cardId: ownProp.params.cardId }),
    attachments: selectAttachments(),
    comments: selectComments(),
    log: selectLog(),
    isUploading: selectIsUploadingAttachments(),
    isDeleting: selectIsDeletingAttachments(),
    possibleResponsible: selectPossibleResponsible(),
    roomMembers: selectRoomMembers(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({ ...cardActions, ...cardsActions, ...boardsActions }, dispatch),
});

const CardUpdateContainer = ({
  attachments,
  board,
  card,
  isUploading,
  isDeleting,
  comments,
  log,
  possibleResponsible,
  roomMembers,
  actions,
}: {
  board: Board;
  card: Question;
  log: any;
  attachments: any;
  isUploading: boolean;
  isDeleting: boolean;
  comments: Comment[];
  roomMembers: any;
  possibleResponsible: User[];
  actions: any;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<FormikProps<any>>();
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);
  const { communityId, roomId, boardId, cardId } = useParams();
  const hasResponsible = !!hasResponsibleField({ fieldConfig: board.field_config });

  useEffect(() => {
    actions
      .fetchComments({ communityId, roomId, boardId, cardId })
      .then((action: any) => alertAction({ action, error: intl.get('common.comments.fetch.error') }));
    actions
      .fetchLog({ communityId, roomId, boardId, cardId })
      .then((action: any) => alertAction({ action, error: intl.get('common.log.fetch.error') }));
    actions
      .fetchAttachments({ communityId, roomId, boardId, cardId })
      .then((action: any) => alertAction({ action, error: intl.get('common.attachments.fetch.error') }));

    if (hasResponsible) {
      const disciplines = card.fields
        .find((field) => field?.id === board.field_config.to_member_field)
        .value.map((discipline) => discipline.id);

      handleFetchPossibleResponsible({ disciplines });
    }
  }, [communityId, roomId, boardId, cardId, actions]);

  const handleSubmit = ({ values, item }: { values: { [id: string]: any }; item: Card }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({
        values,
        item,
        fieldsConfig: board.field_config.fields,
        fieldChangePredicate: isFieldChanged,
      }),
    };

    return actions.updateCard({ communityId, roomId, boardId, cardId: item.id, data }).then((action: any) => {
      alertAction({
        action,
        error: intl.get('card.update.error'),
        success: intl.get('card.update.success'),
        onSuccess: () =>
          navigate(
            withViewModeAndStepQuery(
              closeOnSuccess
                ? `/${communityId}/${roomId}/boards/${boardId}`
                : `/${communityId}/${roomId}/boards/${boardId}/cards/${action.payload.card.id}`,
            ),
          ),
      });
    });
  };

  const handleFetchPossibleResponsible = ({ disciplines }: { disciplines: string[] }) => {
    if (hasResponsible) {
      actions
        .fetchPossibleResponsible({ communityId, roomId, boardId, disciplines })
        .then((action: Action) => alertAction({ action, error: intl.get('card.responsible.error') }));
    }
  };

  const handleStepChange = async ({ card, step }: { card: Card; step: Step }) => {
    const form = formRef.current;

    if (form.dirty) {
      if (!form.isValid) {
        alertAction({
          action: { payload: { error: 'Invalid form' } },
          error: intl.get('card.move.error'),
        });
        return Promise.reject('Form is invalid');
      }

      return handleMoveAndUpdate({ card, step });
    }

    return handleMove({ card, step });
  };

  const handleMoveAndUpdate = ({ card, step }: { card: Card; step: Step }) => {
    const form = formRef.current;

    const updatedValues = getUpdatedFieldsAndValues({
      values: form.values,
      item: card,
      fieldsConfig: board.field_config.fields,
      fieldChangePredicate: isFieldChanged,
    });

    return actions
      .updateCardImmediately({ communityId, roomId, boardId, card, stepId: step.id, data: updatedValues || [] })
      .then((action: any) => {
        alertAction({
          action,
          error: intl.get('card.move.error'),
          success: intl.get('card.move_and_update.success'),
          onSuccess: () => navigate(withViewModeAndStepQuery(`/${communityId}/${roomId}/boards/${boardId}`)),
        });
      });
  };

  const handleMove = ({ card, step }: { card: Card; step: Step }) => {
    const view = new URLSearchParams(location.search).get('view');

    return actions.moveCard({ communityId, roomId, board, cardId: card.id, stepId: step.id }).then((action: any) =>
      alertAction({
        action,
        error: intl.get('card.move.error'),
        success: intl.get('card.move.success'),
        onSuccess: () => navigate(`/${communityId}/${roomId}/boards/${boardId}${view ? `?view=${view}` : ''}`),
      }),
    );
  };

  const handleCancel = () => {
    navigate(withViewModeAndStepQuery(`/${communityId}/${roomId}/boards/${boardId}`));
  };

  const handleUploadAttachment = ({ files }: { files: File[] }) => {
    if (files.length > 0) {
      actions
        .uploadAttachment({ communityId, roomId, boardId: boardId, cardId: card.id, files })
        .then((action: Action) =>
          alertAction({
            action,
            error: intl.get('common.attachments.upload.error'),
            success: intl.get('common.attachments.upload.success'),
          }),
        );
    }
  };

  const handleAddLinks = (links: any[]) => {
    links.forEach((link) =>
      actions.addLink({ communityId, roomId, boardId: board.id, cardId: card.id, link }).then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.link.add.error'),
          success: intl.get('common.link.add.success'),
        }),
      ),
    );
  };

  const handleDeleteAttachment = ({ attachmentId }: { attachmentId: string }) => {
    actions.deleteAttachment({ communityId, roomId, boardId, cardId: card.id, attachmentId }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.attachments.delete.error'),
        success: intl.get('common.attachments.delete.success'),
      }),
    );
  };

  const handleDownloadAttachment = ({ attachment }: { attachment: any }) => {
    let attachmentId = attachment.id;
    let version;
    if (attachment.type === 'internal-link') {
      attachmentId = attachment.destination;
      version = attachment.destination_version;
    }

    downloadCardAttachment({ communityId, roomId, boardId, cardId, attachmentId, version }).then((response) => {
      saveAs(response.data, attachment.name);
    });
  };

  const handleSubmitComment = ({ content }: { content: string }) =>
    actions.createComment({ communityId, roomId, boardId, cardId, content }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.comments.add.error'),
        success: intl.get('common.comments.add.success'),
      }),
    );

  const handleUpdateComment = ({ comment }: { comment: Comment }) =>
    actions
      .updateComment({ communityId, roomId, boardId, cardId, commentId: comment.id, comment })
      .then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.comments.update.error'),
          success: intl.get('common.comments.update.success'),
        }),
      );

  const handleDeleteComment = ({ comment }: { comment: Comment }) =>
    actions.deleteComment({ communityId, roomId, boardId, cardId, commentId: comment.id }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.comments.delete.error'),
        success: intl.get('common.comments.delete.success'),
      }),
    );

  const handleSaveClick = ({ closeForm }: { closeForm: boolean }) => {
    setCloseOnSuccess(closeForm);

    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  if (!card) {
    return <Error text={intl.get('card.not_found', { cardId, boardId })} />;
  }

  return (
    <PageWrapper title={intl.get('app_bar.question_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={card}
            possibleResponsible={possibleResponsible}
            initialValues={populateInitialValues({
              item: card,
              fields: board.field_config.fields,
            })}
            disabledFields={calculateDisabledFieldsForCard({
              permissions: card.permissions,
              fieldConfig: board.field_config,
            })}
            requiredFields={getRequiredFields({
              item: card,
              fields: board.field_config.fields,
            })}
            type={board.type}
            fieldConfig={board.field_config}
            onFetchPossibleResponsible={handleFetchPossibleResponsible}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.answered_date_field === field.id) {
                  return { disableFuture: true };
                }
              },
              string: (fieldConfig, field) => {
                // question/answer field should be displayed as textarea if it's plain text field
                return (field.id === fieldConfig.question_field || field.id === fieldConfig.answer_field) &&
                  field.type === 'string'
                  ? { variant: 'outlined', multiline: true, minRows: 5, maxRows: 10 }
                  : {};
              },
              numeric: () => ({}),
            }}
            formValidation={{
              answered_date_field: (fieldConfig, values) => validateAnsweredDateField({ fieldConfig, values }),
              answer_field: (fieldConfig, values) => validateAnswerField({ fieldConfig, values }),
            }}
            metaInfo={getCardMetaInfo(card, roomMembers)}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                default: true,
                handleClick: () => handleSaveClick({ closeForm: true }),
              },
              {
                title: intl.get('common.form.save'),
                handleClick: () => handleSaveClick({ closeForm: false }),
              },
            ]}
          />
          <CardAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={card}
            attachments={attachments}
            members={roomMembers}
            isUploading={isUploading}
            isDeleting={isDeleting}
            onAddLinks={handleAddLinks}
            onUploadAttachment={handleUploadAttachment}
            onDeleteAttachment={handleDeleteAttachment}
            onDownloadAttachment={handleDownloadAttachment}
          />
          <Log log={log} members={roomMembers} item={card} />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <CardStep card={card} steps={board.step_config.steps} onStepChange={handleStepChange} />
          <CardComments
            board={board}
            item={card}
            comments={comments}
            onSubmitComment={handleSubmitComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            members={roomMembers}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

const ConnectedCardUpdateContainer = connect(mapStateToProps, mapDispatchToProps)(CardUpdateContainer);

export default ConnectedCardUpdateContainer;
